import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Printer } from 'lucide-react';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import leiloesService from '../../services/leiloesService';
import { ordemEntradaService } from '../../services/ordemEntradaService';
import { lotesService } from '../../services/lotesService';
import CatalogoBase from '../../components/catalogos/CatalogoBase';
import { usePdfGenerator } from '../../hooks/usePdfGenerator';
import catalogoConfigService from '../../services/catalogoConfigService';
import CatalogoLeilaoButton from '../../components/catalogos/CatalogoLeilaoButton';

const formatarPercentual = (pct) => Number.isInteger(pct) ? pct : parseFloat(pct.toFixed(1));

const CatalogoLeilao = () => {
  const { leilaoId } = useParams();
  const navigate = useNavigate();
  const { organizadorAtivo } = useOrganizador();
  const [leilao, setLeilao] = useState(null);
  const [leiloes, setLeiloes] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [modoPeso, setModoPeso] = useState('peso_arroba'); // 'peso_arroba' | 'peso'
  const [pesoSemDecimais, setPesoSemDecimais] = useState(false);
  const { generateCatalogoPDF } = usePdfGenerator();

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarLeiloes();
    }
  }, [organizadorAtivo]);

  useEffect(() => {
    if (leilaoId && organizadorAtivo?.id) {
      carregarDados();
    }
  }, [leilaoId, organizadorAtivo]);

  const carregarLeiloes = async () => {
    try {
      const data = await leiloesService.getByOrganizador(organizadorAtivo.id);
      setLeiloes(data || []);
    } catch (error) {
      console.error('Erro ao carregar leilões:', error);
    }
  };

  const handleLeilaoChange = (novoLeilaoId) => {
    if (novoLeilaoId !== leilaoId) {
      navigate(`/catalogos/leilao/${novoLeilaoId}`);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [leilaoData, ordemData, lotesCompletos, configData] = await Promise.all([
        leiloesService.getById(organizadorAtivo.id, leilaoId),
        ordemEntradaService.listarOrdem(leilaoId).catch(() => []),
        lotesService.listarLotesPorLeilao(organizadorAtivo.id, leilaoId),
        catalogoConfigService.getConfig(organizadorAtivo.id).catch(() => null)
      ]);
      
      let lotesComDados;

      if (ordemData && ordemData.length > 0) {
        const idsNaOrdem = new Set(ordemData.map(o => o.lote_id));
        const lotesOrdenados = ordemData
          .map(itemOrdem => ({
            ...lotesCompletos.find(l => l.id === itemOrdem.lote_id),
            ...itemOrdem,
          }))
          .sort((a, b) => a.posicao - b.posicao);

        const lotesFora = lotesCompletos
          .filter(l => !idsNaOrdem.has(l.id))
          .sort((a, b) => (parseInt(a.numero_lote) || 0) - (parseInt(b.numero_lote) || 0));

        const offset = lotesOrdenados.length;
        const lotesForaMapeados = lotesFora.map((lote, i) => ({
          ...lote,
          posicao: offset + i + 1,
          lote_id: lote.id,
          lote_numero: lote.numero_lote,
        }));

        lotesComDados = [...lotesOrdenados, ...lotesForaMapeados];
      } else {
        lotesComDados = lotesCompletos
          .sort((a, b) => (parseInt(a.numero_lote) || 0) - (parseInt(b.numero_lote) || 0))
          .map((lote, index) => ({ ...lote, posicao: index + 1 }));
      }
      
      setLeilao(leilaoData);
      setLotes(lotesComDados);
      setConfig(configData);
      if (configData?.modo_peso) {
        setModoPeso(configData.modo_peso);
      }
      setPesoSemDecimais(!!configData?.peso_sem_decimais);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Idade calculada a partir da data de nascimento do animal (fluxo cavalo —
  // LoteAnimal não tem cadastro de Idade como o lote de gado). Mesma lógica
  // de reports/animal_spec_utils.py:idade_label().
  const idadeLabel = (dataNascimento) => {
    if (!dataNascimento) return '-';
    const nasc = new Date(dataNascimento);
    const hoje = new Date();
    let meses = (hoje.getFullYear() - nasc.getFullYear()) * 12 + (hoje.getMonth() - nasc.getMonth());
    if (hoje.getDate() < nasc.getDate()) meses -= 1;
    meses = Math.max(0, meses);
    if (meses < 24) return `${meses} MES${meses !== 1 ? 'ES' : ''}`;
    const anos = Math.floor(meses / 12);
    return `${anos} ANO${anos !== 1 ? 'S' : ''}`;
  };

  // Formatar peso conforme modo selecionado
  const formatarPeso = (item) => {
    const peso = item._animal ? item._animal.peso : (item.peso_medio || item.peso);
    if (!peso) return '-';
    const pesoNum = parseFloat(peso);
    const pesoFmt = pesoSemDecimais
      ? pesoNum.toFixed(0)
      : pesoNum.toFixed(2).replace('.', ',');
    if (modoPeso === 'peso_arroba') {
      const arrobas = (pesoNum / 30).toFixed(2);
      return `${pesoFmt} - ${arrobas.replace('.', ',')}@`;
    }
    return pesoFmt;
  };

  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo';

  // Ordem fixa das colunas — cavalo prioriza a identidade do animal (nome+%,
  // registro, raça, nascimento, genealogia) antes do vendedor, espelhando
  // ORDEM_COLUNAS_CAVALO/DEFAULT_COLUNAS_CAVALO do backend.
  const ordemColunasGado = ['ordem', 'lote', 'vendedor', 'quantidade', 'tipo', 'raca', 'sexo', 'idade', 'peso', 'curral', 'observacoes', 'valor_inicial', 'pelagem', 'registro', 'percentual_venda', 'localizacao'];
  const ordemColunasCavalo = ['ordem', 'lote', 'animal', 'registro', 'raca', 'data_nascimento', 'pai', 'mae', 'idade', 'sexo', 'tipo', 'peso', 'curral', 'vendedor', 'observacoes', 'valor_inicial', 'pelagem', 'percentual_venda', 'localizacao'];
  const ordemColunas = isFluxoCavalo ? ordemColunasCavalo : ordemColunasGado;
  const colunasDefaultFallback = isFluxoCavalo
    ? ['lote', 'quantidade', 'animal', 'raca', 'data_nascimento', 'pai', 'mae', 'vendedor', 'observacoes']
    : ['lote', 'vendedor', 'quantidade', 'raca', 'sexo', 'idade', 'peso', 'observacoes'];

  // Filtrar apenas as colunas ativas mantendo a ordem
  const colunasAtivas = ordemColunas.filter(col =>
    config?.colunas_ativas?.includes(col) ||
    (!config && colunasDefaultFallback.includes(col))
  );

  // Fluxo cavalo: lote sem tipo_animal (cada animal tem seus próprios dados
  // em lote.animais) — mesmo sinal usado no backend (eh_cavalo). Desdobra 1
  // lote com N animais em N linhas, cada uma trazendo o lote + o animal.
  const SEXO_LABEL = { M: 'Macho', F: 'Fêmea', C: 'Castrado' };
  const ehCavalo = (lote) => !lote.tipo_animal;
  const temDestaque = (item) => item._animal && Math.abs(parseFloat(item._animal.percentual_venda ?? 100) - 100) > 0.001;
  const linhasExpandidas = lotes.flatMap((lote) => {
    const animais = ehCavalo(lote) ? (lote.animais || []) : [];
    if (animais.length > 0) {
      return animais.map((animal) => ({ ...lote, _animal: animal }));
    }
    return [{ ...lote, _animal: null }];
  });
  // Cor de fundo alternada por lote (não por linha) — linhas de animais do
  // mesmo lote cavalo saem com a mesma cor.
  const corPorLote = {};
  let _proxPar = true;
  linhasExpandidas.forEach((item) => {
    if (!(item.id in corPorLote)) {
      corPorLote[item.id] = _proxPar;
      _proxPar = !_proxPar;
    }
  });

  const temObservacoes = colunasAtivas.includes('observacoes');
  const obsSempreAbaixo = temObservacoes && !!config?.observacao_sempre_abaixo;
  // Colunas realmente exibidas na tabela (obs sai da tabela quando "sempre
  // abaixo", ou sempre no fluxo cavalo — lá ela vem combinada com a
  // localização do animal numa linha abaixo, nunca como coluna inline).
  const colunasTabela = (obsSempreAbaixo || isFluxoCavalo)
    ? colunasAtivas.filter(col => col !== 'observacoes')
    : colunasAtivas;

  // Observação + localização, mesma linha. Gado: observação do lote (de
  // sempre). Cavalo: observação do PRÓPRIO ANIMAL (informacoes, não
  // lote.observacoes) + sua localização — espelha
  // reports/catalogos/catalogo_leilao.py:_obs_mais_localizacao().
  const obsMaisLocalizacao = (item) => {
    if (!item._animal) {
      const obs = renderCelulaValor(item, 'observacoes');
      return obs !== '-' ? obs : '';
    }
    const obs = (item._animal.informacoes || '').trim();
    const local = (item._animal.localizacao || '').trim();
    const partes = [];
    if (obs) partes.push(`OBS. ${obs}`);
    if (local) partes.push(`LOCAL ${local.toUpperCase()}`);
    return partes.join('  ·  ');
  };
  const lotesPrimeiraPagina = 33;
  const lotesProximasPaginas = 38;

  // Definir larguras proporcionais das colunas (estilo da referência)
  const getColWidth = (colunaId) => {
    // Vendedor mais estreito só no cavalo — a identidade do animal ocupa
    // mais colunas ali (mesmo override de largura do backend, ver
    // _COLUNAS_DEF_CAVALO em catalogo_leilao.py).
    if (colunaId === 'vendedor' && isFluxoCavalo) return '10%';
    if (temObservacoes && !obsSempreAbaixo) {
      // Com observações: colunas bem compactas + obs ocupa ~40% restante
      const widths = {
        ordem: '3%',
        lote: '5%',
        vendedor: '13%',
        quantidade: '3%',
        tipo: '7%',
        raca: '7%',
        sexo: '3%',
        idade: '6%',
        peso: '9%',
        curral: '4%',
        observacoes: '25%',
        valor_inicial: '6%',
        pelagem: '6%',
        registro: '6%',
        percentual_venda: '4%',
        localizacao: '8%',
        animal: '17%',
        pai: '8%',
        mae: '8%',
        data_nascimento: '6%',
      };
      return widths[colunaId] || '5%';
    } else {
      const widths = {
        ordem: '5%',
        lote: '7%',
        vendedor: '22%',
        quantidade: '6%',
        tipo: '12%',
        raca: '12%',
        sexo: '6%',
        idade: '8%',
        peso: '12%',
        curral: '7%',
        valor_inicial: '10%',
        pelagem: '8%',
        registro: '8%',
        percentual_venda: '5%',
        localizacao: '10%',
        animal: '20%',
        pai: '10%',
        mae: '10%',
        data_nascimento: '8%',
      };
      return widths[colunaId] || '8%';
    }
  };

  // Labels das colunas
  const getColLabel = (colunaId) => {
    const pesoLabel = modoPeso === 'peso_arroba' ? 'PESO (Kg-@)' : 'PESO (Kg)';
    const labels = {
      ordem: 'O.D',
      lote: 'LOTE',
      vendedor: 'VENDEDOR',
      quantidade: 'QTD',
      tipo: 'TIPO',
      raca: 'RAÇA',
      sexo: 'SEXO',
      idade: 'IDADE',
      peso: pesoLabel,
      curral: 'CURRAL',
      observacoes: 'OBSERVAÇÃO',
      valor_inicial: 'VALOR',
      pelagem: 'PELAGEM',
      registro: 'REGISTRO',
      percentual_venda: '% VENDA',
      localizacao: 'LOCALIZAÇÃO',
      animal: 'ANIMAL',
      pai: 'PAI',
      mae: 'MÃE',
      data_nascimento: 'NASC.',
    };
    return labels[colunaId] || colunaId;
  };

  // Renderizar valor da célula
  const renderCelulaValor = (item, colunaId) => {
    switch (colunaId) {
      case 'ordem':
        return <span className="font-bold">{item.posicao}</span>;
      case 'lote':
        return <span className="font-bold">{item.numero_lote}</span>;
      case 'vendedor': {
        const nome = item.fazenda?.nome || item.fazenda_ficticia || item.vendedor?.nome || '-';
        return nome.length > 18 ? nome.substring(0, 18) + '…' : nome;
      }
      case 'quantidade':
        return <span className="font-bold">{item._animal ? '1' : (item.qtd || '-')}</span>;
      case 'tipo': {
        const tipoObj = item._animal ? item._animal.tipo_animal : item.tipo_animal;
        let nome = tipoObj?.nome || '-';
        if (nome.toUpperCase().includes('VACA COM CRIA')) {
          nome = nome.toUpperCase().replace('VACA COM CRIA', 'VACA C/ CRIA');
        }
        return nome;
      }
      case 'raca': {
        const racaObj = item._animal ? item._animal.raca : item.raca;
        if (!racaObj?.nome) return '-';
        const nome = racaObj.nome.toUpperCase();
        if (!item._animal) {
          // Gado: truncamento de sempre, sem mudança de comportamento.
          return nome.length > 10 ? nome.substring(0, 10) + '…' : nome;
        }
        // Cavalo: nome longo abrevia só a 1ª palavra pra inicial
        // ("MANGALARGA MARCHADOR" -> "M. MARCHADOR"), sem cortar no meio.
        const partes = nome.split(' ');
        if (partes.length > 1 && nome.length > 14) {
          return `${partes[0][0]}. ${partes.slice(1).join(' ')}`;
        }
        return nome;
      }
      case 'sexo': {
        const sexo = item._animal ? SEXO_LABEL[item._animal.sexo] || item._animal.sexo : item.sexo;
        return sexo === 'Macho' ? 'M' : sexo === 'Fêmea' ? 'F' : sexo || '-';
      }
      case 'idade': {
        if (item._animal) {
          return idadeLabel(item._animal.data_nascimento);
        }
        return item.idade?.nome || '-';
      }
      case 'peso':
        return formatarPeso(item);
      case 'curral':
        return item.curral || '-';
      case 'pelagem':
        return item._animal?.pelagem?.toUpperCase() || '-';
      case 'registro':
        return item._animal?.registro?.trim() ? <span className="font-bold">{item._animal.registro.toUpperCase()}</span> : '-';
      case 'percentual_venda':
        return item._animal ? `${formatarPercentual(parseFloat(item._animal.percentual_venda ?? 100))}%` : '-';
      case 'localizacao':
        return item._animal?.localizacao?.toUpperCase() || '-';
      case 'animal': {
        if (!item._animal) return '-';
        const nome = (item._animal.nome || '-').toUpperCase();
        const pct = parseFloat(item._animal.percentual_venda ?? 100);
        const linha1 = Math.abs(pct - 100) > 0.001 ? `${formatarPercentual(pct)}% ${nome}` : nome;
        // Registro só embutido embaixo do nome quando NÃO é coluna própria
        // (evita duplicar quando "Registro" está marcado separadamente).
        const registroComoColuna = colunasAtivas.includes('registro');
        const registro = !registroComoColuna ? (item._animal.registro || '').trim() : '';
        if (!registro) return <span className="font-bold">{linha1}</span>;
        return (
          <span>
            <span className="font-bold">{linha1}</span>
            <br />
            <span className="font-bold text-gray-500" style={{ fontSize: '0.68rem' }}>{registro}</span>
          </span>
        );
      }
      case 'pai':
        return item._animal?.pai?.trim() ? item._animal.pai.toUpperCase() : '-';
      case 'mae':
        return item._animal?.mae?.trim() ? item._animal.mae.toUpperCase() : '-';
      case 'data_nascimento': {
        if (!item._animal?.data_nascimento) return '-';
        return new Date(item._animal.data_nascimento).toLocaleDateString('pt-BR');
      }
      case 'observacoes': {
        let obs = item.observacoes || '';
        const criasF = item.qtd_crias_femeas || 0;
        const criasM = item.qtd_crias_machos || 0;
        if (criasF > 0 || criasM > 0) {
          const partes = [];
          if (criasF > 0) partes.push(`F:${criasF}`);
          if (criasM > 0) partes.push(`M:${criasM}`);
          const criasTxt = `Crias: ${partes.join(' ')}`;
          obs = obs ? `${criasTxt} | ${obs}` : criasTxt;
        }
        return obs || '-';
      }
      case 'valor_inicial':
        return item.valor_inicial ? `R$ ${parseFloat(item.valor_inicial).toFixed(2)}` : '-';
      default:
        return '-';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="print-area">
      <CatalogoBase
        titulo="CATÁLOGO LEILÃO"
        subtitulo={leilao ? (
          <div>
            <div>{leilao.nome}</div>
            <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-6 text-gray-600 text-sm sm:text-base mt-2">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                {new Date(leilao.data_leilao).toLocaleDateString('pt-BR')}
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} />
                {leilao.cidade}, {leilao.uf}
              </div>
            </div>
          </div>
        ) : null}
        onVoltar={() => window.history.back()}
        logoOrganizador={organizadorAtivo?.logo_url}
        bannerLeilao={leilao?.banner_url ? (leilao.banner_url.startsWith('http') ? leilao.banner_url : `${(import.meta.env.VITE_API_URL || 'http://localhost:8001/').replace(/\/$/, '')}${leilao.banner_url}`) : null}
        landscape={false}
        showHeaderInfo={false}
        leiloes={leiloes}
        selectedLeilao={leilaoId}
        onLeilaoChange={handleLeilaoChange}
        extraButtons={
          leilaoId && organizadorAtivo?.id ? (
            <CatalogoLeilaoButton
              leilaoId={leilaoId}
              organizadorId={organizadorAtivo.id}
              leilaoNome={leilao?.nome || ''}
              label="Imprimir"
              endpoint="catalogo-leilao"
              permitirPaisagem
            />
          ) : null
        }
      >

      {/* No celular a tabela tem colunas demais pra ficar legível — em vez de
          espremer, orienta a usar o botão Imprimir (gera o PDF, que é responsivo). */}
      <div className="sm:hidden text-center py-10 px-4 text-gray-500">
        <Printer size={32} className="mx-auto mb-3 text-gray-400" />
        <p className="font-medium text-gray-700">Visualização em tabela não disponível no celular</p>
        <p className="text-sm mt-1">Use o botão <strong>Imprimir</strong> no topo da página para gerar o PDF do catálogo.</p>
      </div>

      {/* Tabela de Lotes - Estilo Catálogo */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {colunasTabela.map(colunaId => (
              <col key={colunaId} style={{ width: getColWidth(colunaId) }} />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b-2 border-gray-800">
              {colunasTabela.map(colunaId => (
                <th
                  key={colunaId}
                  className="px-2 py-2 text-left font-bold text-xs uppercase tracking-wide text-gray-800 border-b border-gray-400"
                >
                  {getColLabel(colunaId)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhasExpandidas.length > 0 ? (
              linhasExpandidas.map((item, index) => {
                const destaque = temDestaque(item);
                const bg = destaque ? 'bg-amber-100' : (corPorLote[item.id] ? 'bg-white' : 'bg-gray-50');
                const primeiraLinhaDoLote = index === 0 || linhasExpandidas[index - 1].id !== item.id;
                // Cavalo: obs+localização sempre abaixo, por animal (localização
                // muda de animal pra animal, então nunca deduplica por lote).
                const textoAbaixo = item._animal ? obsMaisLocalizacao(item) : (obsSempreAbaixo ? renderCelulaValor(item, 'observacoes') : null);
                const mostrarAbaixo = item._animal ? !!textoAbaixo : (primeiraLinhaDoLote && obsSempreAbaixo && textoAbaixo !== '-');
                return (
                <React.Fragment key={`${item.id}-${item._animal?.id ?? 0}`}>
                  <tr className={`border-b ${(obsSempreAbaixo || item._animal) ? '' : 'border-gray-200'} ${bg}`}>
                    {colunasTabela.map(colunaId => (
                      <td
                        key={colunaId}
                        className={`px-2 py-1.5 text-sm ${destaque ? 'font-bold' : ''} ${(colunaId === 'observacoes' || colunaId === 'pai' || colunaId === 'mae' || ((colunaId === 'vendedor' || colunaId === 'raca') && isFluxoCavalo)) ? 'text-xs' : ''} ${
                          ['ordem', 'quantidade', 'sexo', 'idade', 'curral'].includes(colunaId) ? 'text-center' : ''
                        }`}
                        style={colunaId === 'observacoes' ? { whiteSpace: 'normal', wordBreak: 'break-word' } : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {renderCelulaValor(item, colunaId)}
                      </td>
                    ))}
                  </tr>
                  {mostrarAbaixo && (
                    <tr className={`border-b border-gray-200 ${corPorLote[item.id] ? 'bg-white' : 'bg-gray-50'}`}>
                      <td colSpan={colunasTabela.length} className="px-2 pb-1.5 pt-0 text-sm text-gray-700" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {textoAbaixo}
                      </td>
                    </tr>
                  )}
                  {(() => {
                    if (index + 1 === lotesPrimeiraPagina) {
                      return <tr className="page-break-after"><td colSpan={colunasTabela.length} style={{height: '0px', padding: '0px', border: 'none'}}></td></tr>;
                    } else if (index + 1 > lotesPrimeiraPagina && (index + 1 - lotesPrimeiraPagina) % lotesProximasPaginas === 0) {
                      return <tr className="page-break-after"><td colSpan={colunasTabela.length} style={{height: '0px', padding: '0px', border: 'none'}}></td></tr>;
                    }
                    return null;
                  })()}
                </React.Fragment>
              );})
            ) : (
              <tr>
                <td colSpan={colunasAtivas.length} className="px-3 py-8 text-center text-gray-500">
                  Nenhum lote encontrado na ordem de entrada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totais */}
      {config?.mostrar_totais !== false && (
        <div className="totals-section">
          <div className="totals-grid">
            <div className="total-item">
              <div className="total-number text-gray-900">{lotes.length}</div>
              <div className="total-label">Total de Lotes</div>
            </div>
            <div className="total-item">
              <div className="total-number text-blue-600">
                {linhasExpandidas
                  .filter(l => (l._animal ? SEXO_LABEL[l._animal.sexo] : l.sexo) === 'Macho')
                  .reduce((sum, l) => sum + (l._animal ? 1 : (l.qtd || 0)), 0)}
              </div>
              <div className="total-label">Machos</div>
            </div>
            <div className="total-item">
              <div className="total-number text-pink-600">
                {linhasExpandidas
                  .filter(l => (l._animal ? SEXO_LABEL[l._animal.sexo] : l.sexo) === 'Fêmea')
                  .reduce((sum, l) => sum + (l._animal ? 1 : (l.qtd || 0)), 0)}
              </div>
              <div className="total-label">Fêmeas</div>
            </div>
            <div className="total-item">
              <div className="total-number text-purple-600">
                {linhasExpandidas.reduce((sum, l) => sum + (l._animal ? 1 : (l.qtd || 0)), 0)}
              </div>
              <div className="total-label">Total de Animais</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Rodapé final */}
      {config?.mostrar_rodape !== false && (
        <div className="footer-final">
          www.rematei.com.br | Gerado em {new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
        </div>
      )}

      </CatalogoBase>
    </div>
  );
};

export default CatalogoLeilao;
